<template src="./template.html"></template>
<style lang="scss" src="./styles.scss" scoped></style>

<script setup>
import { ref, computed, nextTick, onMounted, onBeforeUnmount } from 'vue'

const props = defineProps({
  modelValue: [String, Number, Object, null],
  options: {
    type: Array,
    required: true
  },
  label: String,
  placeholder: {
    type: String,
    default: 'Select'
  },
  valueKey: {
    type: String,
    default: 'value'
  },
  labelKey: {
    type: String,
    default: 'label'
  },
  error: String
})

const emit = defineEmits(['update:modelValue', 'clear-error'])

let searchTimeout = null

const root = ref(null)
const isOpen = ref(false)
const optionRefs = ref([])
const dropdown = ref(null)
const searchQuery = ref('')
const highlightedIndex = ref(-1)
const selectedIndex = computed(() =>
  props.options.findIndex(o => isSelected(o))
)

const setOptionRef = (el) => {
  if (el) optionRefs.value.push(el)
}

const toggle = async () => {
  isOpen.value = !isOpen.value

   if (isOpen.value) {
    highlightedIndex.value = selectedIndex.value

    optionRefs.value = []

    await nextTick()
    scrollToIndex(selectedIndex.value)
  }
}

const close = () => {
  isOpen.value = false
}

const getOptionValue = (option) => {
  if (!option) return ''

  return typeof option === 'object'
    ? option[props.valueKey]
    : option
}

const getOptionLabel = (option) => {
  return typeof option === 'object'
    ? option[props.labelKey]
    : option
}

const isSelected = (option) => {
  if (typeof props.modelValue === 'object') {
    return getOptionValue(option) === getOptionValue(props.modelValue)
  }
  return getOptionValue(option) === props.modelValue
}

const selectedOption = computed(() => {
  return props.options.find(option =>
    isSelected(option)
  )
})

const select = (option) => {
  emit('update:modelValue', option)
  close()

  if (option) {
    emit('clear-error')
  }
}

const hasValue = computed(() => {
  return !!selectedOption.value
})

/* Click outside */
const handleClickOutside = (e) => {
  if (root.value && !root.value.contains(e.target)) {
    close()
  }
}

const handleKeydown = (e) => {
  // Open / navigate keys
  if (e.key === 'ArrowDown') {
    e.preventDefault()
    if (!isOpen.value) {
      toggle()
      highlightedIndex.value = selectedIndex.value ?? 0
    } else {
      highlightedIndex.value =
        (highlightedIndex.value + 1) % props.options.length
      nextTick(() => scrollToIndex(highlightedIndex.value))
    }
    return
  }

  if (e.key === 'ArrowUp') {
    e.preventDefault()
    if (!isOpen.value) {
      toggle()
      highlightedIndex.value = selectedIndex.value ?? 0
    } else {
      highlightedIndex.value =
        (highlightedIndex.value - 1 + props.options.length) % props.options.length
      nextTick(() => scrollToIndex(highlightedIndex.value))
    }
    return
  }

  // Enter / Space
  if (e.key === 'Enter' || e.key === ' ') {
    e.preventDefault() // 🔥 prevents page scroll on Space
    if (!isOpen.value) {
      toggle()
      highlightedIndex.value = selectedIndex.value ?? 0
    } else if (highlightedIndex.value >= 0) {
      select(props.options[highlightedIndex.value])
    }
    return
  }

  if (e.key === 'Escape') {
    close()
    return
  }

  // typing support
  if (e.key.length === 1 && !e.ctrlKey && !e.metaKey) {
    findOptionByTyping(e.key)
  }
}

const findOptionByTyping = (char) => {
  searchQuery.value += char.toLowerCase()

  const index = props.options.findIndex(option =>
    getOptionLabel(option)
      .toLowerCase()
      .startsWith(searchQuery.value)
  )

  if (index !== -1) {
    highlightedIndex.value = index
    nextTick(() => scrollToIndex(index))
  }

  clearTimeout(searchTimeout)

  searchTimeout = setTimeout(() => {
    searchQuery.value = ''
  }, 500)
}

const scrollToIndex = (index) => {
  const container = dropdown.value
  const el = optionRefs.value[index]
  if (!container || !el) return

  const elRelativeTop = el.getBoundingClientRect().top - container.getBoundingClientRect().top
  const containerHeight = container.clientHeight

  if (elRelativeTop < 0) {
    container.scrollTop += elRelativeTop
  } else if (elRelativeTop + el.offsetHeight > containerHeight) {
    container.scrollTop += elRelativeTop + el.offsetHeight - containerHeight
  }
}

onMounted(() => {
  document.addEventListener('click', handleClickOutside)
})

onBeforeUnmount(() => {
  document.removeEventListener('click', handleClickOutside)
})
</script>
